import React, { ReactElement, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import Breakpoints from '../../design-utils/breakpoints';
import { getCurrencyFormatter, getGidFromIdForResource } from '../../utils';
import MinusIcon from '../Icons/MinusIcon';
import PlusIcon from '../Icons/PlusIcon';
import StarIcon from '../Icons/Star';
import { H5 } from '../Typography/';
import { SlidePanelProductInfoProps } from '../SlidePanel';
import LazyImage from '../LazyImage';
import { useInventory } from '../../subscriptions/state/InventoryContext';
import { isThereEnoughInventory } from '../../subscriptions/utils';
import { useBundleSetting } from '../../subscriptions/state/BundleSettingContext';
import { applyDiscountsToLine } from '../../subscriptions/utils';
import { FinalFocusRef } from '../SlidePanel/SlidePanel';

const secondaryTextColor = '#637381';
const starColor = '#ffc600';

const CardContainer = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  width: 220px;
  @media screen and (max-width: ${Breakpoints.md}) {
    width: 260px;
  }
  height: auto;
  text-align: center;
`;

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: 500;
  margin: 10px 0;
`;

const CardSubTitle = styled.h5`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  text-transform: none;
  margin: 5px 0;
  color: ${secondaryTextColor};
`;

const CardSubSubTitle = styled(H5)<{
  ref?: React.RefObject<HTMLHeadingElement>;
}>`
  margin: 5px 0;
`;

const CustomMinusIcon = styled(MinusIcon)`
  width: 16px;
  height: 16px;
`;

const CustomPlusIcon = styled(PlusIcon)`
  width: 16px;
  height: 16px;
`;

const CardPrice = styled.p`
  margin: 0;
  margin-top: 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  span {
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    margin-left: 10px;
    color: ${secondaryTextColor};
  }
`;

const CardButton = styled.button`
  margin-top: 10px;
  font-size: max(1em, 12px);
  line-height: max(1em, 20px);
  color: #ffffff;
  font-weight: 700;
  background: #000000;
  text-align: center;
  border-radius: 4px;
  padding: 8px 20px;
  box-sizing: border-box;
  width: auto;
  max-height: 100%;
  width: 100%;
  &:disabled {
    background-color: #dfe3e8;
  }
`;

const QuantityButtonContainer = styled.div<{ available?: boolean }>`
  display: flex;
  justify-content: space-between;
  border: 1px solid #c4cdd5;
  padding: 12px;
  border-radius: 22px;
  font-weight: 400;
  font-size: 14px;
  background: ${({ available }) => (available ? 'transparent' : '#f6f6f6')};
  width: 140px;
  margin: 0 auto;
  max-height: 44px;
`;

const QuantityButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: initial;
  height: initial;

  &:hover {
    background-color: initial;
  }

  &:disabled {
    visibility: hidden;
  }
`;

const StarsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem auto;
  font-size: 1.7rem;
`;

const Reviews = ({
  reviews,
  onClick,
  onKeyDown,
}: {
  reviews: Reviews;
  onClick: (finalFocusRef: FinalFocusRef) => void;
  onKeyDown: (e: React.KeyboardEvent, finalFocusRef: FinalFocusRef) => void;
}) => {
  const stars = Array.from(
    { length: Math.ceil(Number(reviews?.average) || 0) },
    (_, i) => i + 1,
  );
  const { t } = useTranslation();
  const starsRef = useRef<HTMLDivElement>(null);

  return (
    <StarsContainer
      className="bundle-product-card__stars-container"
      role="button"
      aria-label={t('aria_labels.stars_number', { stars_qty: stars.length })}
      tabIndex={0}
      onKeyDown={(e) => onKeyDown(e, starsRef)}
      onClick={() => onClick(starsRef)}
      ref={starsRef}
    >
      {stars.map((star) => (
        <StarIcon fill={starColor} key={star}></StarIcon>
      ))}
    </StarsContainer>
  );
};

const Quantity = styled.span`
  :disabled {
    opacity: 0.3;
  }
`;

export interface ProductCardProps {
  _awtId?: string;
  children?: React.ReactElement;
  onChoose?: (product: GlobalProduct['id']) => void;
  onIncrease?: () => void;
  onDecrease?: () => void;
  onCardClick?: (productInfo: SlidePanelProductInfoProps) => void;
  onChooseText?: string;
  price?: string | number;
  currency?: string;
  id?: number | string;
  handle?: string;
  productId?: number | string;
  image: string | null;
  quantity?: number;
  currentPrice?: number;
  title: string;
  subtitle?: string;
  variantTitle?: string;
  available?: boolean;
  description?: string;
  VariantSelector?: JSX.Element | null;
  doNotApplyDiscount?: boolean;
  reviews?: Reviews;
}

const getOptimisedImageUrl = (image: string | null, size: number) =>
  image ? `${image}&width=${size}` : null;

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  handle,
  productId,
  image,
  quantity = 0,
  price,
  currentPrice,
  title,
  subtitle,
  variantTitle,
  currency,
  available,
  onChoose,
  onChooseText,
  children,
  onIncrease,
  onDecrease,
  onCardClick,
  description,
  VariantSelector,
  doNotApplyDiscount = false,
  reviews,
}) => {
  const { t } = useTranslation();
  const { inventory } = useInventory();
  const { bundleSetting } = useBundleSetting();

  const productVariantId = getGidFromIdForResource(
    id as string,
    'ProductVariant',
  );
  const productVariantInventory = inventory?.[productVariantId];
  const productImage = window?.bundleapp?.settings?.bab?.productImage || 220;
  const variantTitleRef = useRef<HTMLHeadingElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLHeadingElement>(null);

  const handleClick = (finalFocusRef: FinalFocusRef) => {
    if (onCardClick) {
      onCardClick({
        productId,
        image,
        title,
        variantTitle,
        subtitle,
        description,
        reviews,
        handle,
        id,
        finalFocusRef,
      });
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    finalFocusRef: FinalFocusRef,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      handleClick(finalFocusRef);
    }
  };

  if (!id) return null;
  return (
    <CardContainer
      className={`bundle-product-card p_${id} q_${quantity}${
        quantity > 0 ? ' selected' : ''
      } ${
        available || available === undefined
          ? 'bundle-product-card--available'
          : 'bundle-product-card--unavailable'
      }`}
      data-testid="product-card"
      data-pid={id}
      data-quantity={quantity}
      data-available={available}
      role="gridcell"
    >
      <div
        data-testid="product-card-clickable-area"
        style={{ cursor: onCardClick ? 'pointer' : 'unset' }}
        onClick={() => handleClick(titleRef)}
        aria-label={t('aria_labels.product_card', { title })}
        role="button"
      >
        <LazyImage
          image={getOptimisedImageUrl(image, productImage)}
          thumbnail={getOptimisedImageUrl(image, productImage / 10)}
          alt={title}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        />
        {reviews?.average ? (
          <Reviews
            reviews={reviews}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          />
        ) : null}

        {title ? (
          <CardTitle
            className="bundle-product-card__title"
            role="button"
            onClick={() => handleClick(titleRef)}
            onKeyDown={(e) => handleKeyDown(e, titleRef)}
            tabIndex={0}
            ref={titleRef}
          >
            {title}
          </CardTitle>
        ) : null}
        {variantTitle ? (
          <CardSubTitle
            role="button"
            className="bundle-product-card__subtitle"
            onClick={() => handleClick(variantTitleRef)}
            onKeyDown={(e) => handleKeyDown(e, variantTitleRef)}
            tabIndex={0}
            ref={variantTitleRef}
          >
            {variantTitle}
          </CardSubTitle>
        ) : null}
        {subtitle ? (
          <CardSubSubTitle
            className="bundle-product-card__subtitle"
            modifiers={['body', 'subdued']}
            onClick={() => handleClick(subtitleRef)}
            onKeyDown={(e) => handleKeyDown(e, subtitleRef)}
            tabIndex={0}
            ref={subtitleRef}
          >
            {subtitle}
          </CardSubSubTitle>
        ) : null}
        {children}
      </div>
      {VariantSelector ? VariantSelector : null}
      {onIncrease && onDecrease && (
        <QuantityButtonContainer
          className={`${
            available
              ? 'bundle-product-card__quantity-button-container'
              : 'bundle-product-card__quantity-button-container bundle-product-card__quantity-button-container--unavailable'
          }`}
          available={available}
          role="group"
        >
          <QuantityButton
            role="button"
            className="bundle-product-card__quantity-button-container--button"
            data-testid="decrement-quantity"
            disabled={!available}
            onClick={onDecrease}
            aria-label={t('aria_labels.decrease_quantity')}
            tabIndex={0}
          >
            <CustomMinusIcon />
          </QuantityButton>
          <Quantity>
            {available ? quantity : t('product_swap.sold_out')}
          </Quantity>
          <QuantityButton
            className="bundle-product-card__quantity-button-container--button"
            data-testid="increment-quantity"
            role="button"
            disabled={
              !available ||
              !isThereEnoughInventory({
                inventory: productVariantInventory,
                quantity,
              })
            }
            onClick={onIncrease}
            aria-label={t('aria_labels.increase_quantity')}
            tabIndex={0}
          >
            <CustomPlusIcon />
          </QuantityButton>
        </QuantityButtonContainer>
      )}
      {onChoose ? (
        <CardButton
          role="button"
          aria-label={t('aria_labels.choose_button', { title })}
          className="bundle-product-card__button"
          onClick={() => onChoose(id)}
          disabled={quantity <= 0}
          data-testid="choose-button"
          tabIndex={0}
        >
          {quantity <= 0
            ? t('product_swap.sold_out')
            : onChooseText
              ? onChooseText
              : t('product_swap.choose')}
        </CardButton>
      ) : null}
      {price &&
        getPrice({
          price,
          currency,
          currentPrice,
          bundleSetting,
          doNotApplyDiscount,
        })}
    </CardContainer>
  );
};

export default ProductCard;

interface IGetPrice {
  price: string | number;
  currentPrice?: number;
  currency?: string;
  bundleSetting?: BundleSetting;
  doNotApplyDiscount?: boolean;
}
const getPrice = ({
  price,
  currentPrice,
  currency,
  bundleSetting,
  doNotApplyDiscount = false,
}: IGetPrice): ReactElement => {
  const format = getCurrencyFormatter(currency);
  const formattedPrice = format(currentPrice as number);
  const priceWithDiscount = doNotApplyDiscount
    ? price
    : applyDiscountsToLine({
        price,
        quantity: 1,
        discounts: [bundleSetting].filter(Boolean),
      });

  return (
    <CardPrice className="bundle-product-card__price">
      {format(priceWithDiscount)}{' '}
      {currentPrice && currentPrice > priceWithDiscount ? (
        <span style={{ textDecoration: 'line-through' }}>{formattedPrice}</span>
      ) : null}
    </CardPrice>
  );
};
